import type * as ents from '@attain-apps/entities'

import type * as dms from '../../../dms'

import type { Plugin } from './plugin'
import { State } from './state'

/** Selectors for configurable Space dimension filter features. */
export class Selectors<
  TTypeParams extends Plugin.TypeParams = Plugin.TypeParams,
> {
  /** Deepest non-empty selection in the configured dimension. */
  public readonly deepestSelection

  /** Lookup of option state keyed by DMS entity ID. */
  public readonly optionsLookup

  /** Retrieves option state for one entity, defaulting to idle. */
  public readonly optionsFor

  /** Retrieves selected record IDs for one entity, defaulting to empty. */
  public readonly selectionFor

  /** In-memory selections keyed by DMS entity ID. */
  public readonly selectionLookup

  constructor(
    plugin: Plugin<TTypeParams>,
    selectionStorage: Plugin['selectionStorage'],
    schemaFeature: dms.schema.Feature,
  ) {
    this.optionsLookup = plugin.feature.state.project(
      state => state.optionsLookup,
    )
    this.selectionLookup = selectionStorage.selectors.lookup
    this.deepestSelection = this.selectionLookup.project(
      schemaFeature.selectors.schema.asSelector,
      (
        selectionLookup,
        schema,
      ): ents.extra.space.Record.Filter.DimensionFilter | undefined => {
        if (!schema.tryGetDimension(plugin.dimensionId)) {
          return undefined
        }

        const deepestEntity = schema
          .getEntitiesInDimension(plugin.dimensionId)
          .filter(entity => selectionLookup[entity.id]?.length)
          .reduce<ents.core.dms.models.Entity | undefined>(
            (deepest, entity) =>
              !deepest || entity.level > deepest.level ? entity : deepest,
            undefined,
          )
        const recordIds = deepestEntity
          ? selectionLookup[deepestEntity.id]
          : undefined

        return deepestEntity && recordIds?.length
          ? { entityId: deepestEntity.id, recordIds }
          : undefined
      },
    )
    this.optionsFor = plugin.feature.createSelectorMethod(
      (entityId: ents.core.dms.models.Entity.Id) =>
        this.optionsLookup.project(
          lookup => lookup[entityId] ?? State.IDLE_OPTIONS,
        ),
    )
    this.selectionFor = plugin.feature.createSelectorMethod(
      (entityId: ents.core.dms.models.Entity.Id) =>
        this.selectionLookup.project(lookup => lookup[entityId] ?? []),
    )
  }
}
